<template>
  <div>
    <UnauthorizedBlock>
      <div class="flex items-end mb-14">
        <div class="text-xl font-bold text-text leading-tight">
          {{ $t('title') }}
        </div>
        <router-link class="text-2sm text-text-label leading-tight ml-auto
                          transition duration-200 hover:text-red"
                     :to="{name:'login'}">
          {{ $t('loginLink') }}
        </router-link>
      </div>
      <div v-if="!enabledReg" class="text-md font-medium text-text leading-tight">
        {{ $t('regDisabledText') }}
      </div>
      <form v-else @submit.prevent=""
            class="grid grid-cols-1 row-gap-8">
        <BaseInput v-model.trim="$v.email.$model"
                   :label="$t('emailLabel')"
                   :class="{'--error':$v.email.$error || !!serverErrors.email}">
          <template v-slot:errors>
            <div class="error" v-if="!$v.email.email">{{ $t('errors.emailIncorrect') }}</div>
            <div class="error" v-else-if="!$v.email.required">{{ $t('errors.required') }}</div>
            <div class="error" v-if="$v.email.required && !!serverErrors.email">{{ serverErrors.email }}</div>
          </template>
        </BaseInput>
        <PasswordInput v-model.trim="$v.password.$model"
                       type="password"
                       :label="$t('passwordLabel')"
                       :password-reveal="true"
                       :error="$v.password.$error || !!serverErrors.password">
          <template v-slot:errors>
            <div class="error" v-if="!$v.password.required">{{ $t('errors.required') }}</div>
            <div class="error" v-if="!$v.password.minLength">{{ $t('errors.passwordMinLength') }}</div>
            <div class="error" v-if="($v.password.minLength || $v.password.required) && !!serverErrors.password">
              {{ serverErrors.password }}
            </div>
          </template>
        </PasswordInput>
        <template v-if="!!password">
          <PasswordInput v-model.trim="$v.repeat_password.$model"
                         type="password"
                         :label="$t('repeatPasswordLabel')"
                         :password-reveal="true"
                         :error="$v.repeat_password.$error">
            <template v-slot:errors="">
              <div class="error" v-if="!$v.repeat_password.required">{{ $t('errors.required') }}</div>
              <div class="error" v-else-if="!$v.repeat_password.sameAsPassword">
                {{ $t('errors.samePassword') }}
              </div>
            </template>
          </PasswordInput>
        </template>
        <AgreeTermsAndPolitics/>
        <!--      <div class="form-container__common-error" v-if="!!serverErrors.error">-->
        <!--        <div>{{ serverErrors.error }}</div>-->
        <!--      </div>-->
        <BaseButton theme="red" class="w-full" @click="reg" :loading="submitted">
          {{ $t('register') }}
        </BaseButton>
        <LoginOAuth/>
      </form>
    </UnauthorizedBlock>
    <RecaptchaBranding/>
  </div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";
import {required, email, sameAs, minLength, requiredIf} from 'vuelidate/lib/validators'
import UnauthorizedBlock from "@/components/layouts/common/UnauthorizedBlock";
import BaseInput from "@/components/UI/BaseInput/BaseInput";
import PasswordInput from "@/components/UI/BaseInput/PasswordInput";
import AgreeTermsAndPolitics from "@/components/common/AgreeTermsAndPolitics";
import BaseButton from "@/components/UI/BaseButton";
import RecaptchaBranding from "@/components/common/RecaptchaBranding";
import LoginOAuth from "@/views/guests/Login/LoginOAuth";

export default {
  name: "Register",
  components: {
    LoginOAuth,
    RecaptchaBranding,
    BaseButton,
    AgreeTermsAndPolitics,
    PasswordInput,
    BaseInput,
    UnauthorizedBlock,
  },
  data() {
    return {
      enabledReg: true,
      email: '',
      password: '',
      repeat_password: '',
      submitted: false,
      serverErrors: {
        error: '',
        username: '',
        email: '',
        password: ''
      }
    }
  },
  validations: {
    email: {
      required,
      email
    },
    password: {
      required,
      minLength: minLength(6)
    },
    repeat_password: {
      required: requiredIf(function () {
        return this.password !== ''
      }),
      sameAsPassword: sameAs('password')
    }
  },
  computed: {
    ...mapState('auth', ['fp']),
    ...mapState('i18n', ['lang']),
  },
  methods: {
    ...mapActions('auth', ['getStatus', 'register']),
    ...mapMutations('auth', ['setAccessToken', 'setRefreshToken']),
    ...mapActions('auth', ['getStatus', 'login']),
    toggleAgree(e) {
      this.agree = e;
      if (e && !!this.agree_error) {
        this.agree_error = null;
      }
    },
    showErrors(messages) {
      if (typeof messages === 'string') {
        this.serverErrors.error = messages;
      }
      !!messages.username ? this.serverErrors.username = messages.username : '';
      !!messages.email ? this.serverErrors.email = messages.email : '';
      !!messages.password ? this.serverErrors.password = messages.password : '';
    },
    clearErrors() {
      this.serverErrors = {error: '', username: '', email: '', password: ''}
    },
    async loginUser() {
      let username = this.email,
          password = this.password,
          fingerprint = this.fp.result;
      try {
        const res = await this.login({username, password, fingerprint})
        if (res.data.success) {
          const a_token = res.body.data.access_token,
              r_token = res.body.data.refresh_token;
          this.setRefreshToken(r_token)
          this.setAccessToken(a_token)
          this.$router.push('/office');
        } else {
          this.$toasted.error('Аутентификация не пройдена, попробуйте войти самостоятельно')
        }
      } catch (err) {
        console.log(err)
        this.$toasted.info('Ошибка! Что-то пошло не так...');
      } finally {
        this.email = '';
        this.password = '';
        this.repeat_password = '';
        this.$v.$reset();
      }
    },
    async reg() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.submitted = true;
        let regData = {
          username: this.username,
          email: this.email,
          password: this.password,
          language: this.lang || this.$i18n.locale
        }
        try {
          await this.$recaptchaLoaded()
          regData.recaptcha_response = await this.$recaptcha('signup')
          const res = await this.register(regData);
          if (res.body.success) {
            this.$toasted.success('Вы успешно зарегистрировались');
            setTimeout(async () => {
              this.$toasted.info('Заходим в личный кабинет...');
              await this.loginUser();
            }, 500);
          } else {
            this.submitted = false;
            this.showErrors(res.body.message)
            setTimeout(() => {
              this.clearErrors();
            }, 5000)
          }
        } catch (err) {
          console.log(err)
          this.$toasted.error('Ошибка! Что-то пошло не так...');
        }
      }
    }
  },
  async created() {
    const res = await this.getStatus();
    this.enabledReg = res.body.data.registration;
  },
  i18n: {
    messages: {
      en: {
        title: "Sign Up",
        loginLink: 'Already registered?',
        regDisabledText: 'User registration is temporarily disabled',
        emailLabel: "E-mail",
        passwordLabel: "Password",
        repeatPasswordLabel: "Confirm password",
        agreement: "I have carefully read and accept the conditions\nPrivacy Policies and Rules.",
        register: "Sign Up",
      },
      ru: {
        title: "Регистрация",
        loginLink: 'Уже зарегистрированы?',
        regDisabledText: 'Регистрация пользователей временно ограничена',
        emailLabel: "E-mail",
        passwordLabel: "Пароль",
        repeatPasswordLabel: "Повторите пароль",
        agreement: "Я прочитал и принимаю условия Пользовательского соглашения и Политики конфиденциальности",
        register: "Зарегистрироваться",
      }
    }
  }
}
</script>
